import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  type Unsubscribe
} from "firebase/firestore";
import { db } from "./firebase";

export type CalendarRepeat = "none" | "daily" | "weekly" | "monthly" | "yearly" | "recurring";

export type FamilyCalendarEvent = {
  id: string;
  title: string;
  dateKey: string;
  endDateKey?: string | null;
  time?: string | null;
  endTime?: string | null;
  repeat: CalendarRepeat;
  location?: string;
  details?: string;
  participantUids?: string[];
  showOnHome: boolean;
  source: "manual" | "apple" | "google";
  sourceConnectionId?: string;
  externalEventId?: string;
  createdBy: string;
};

export type CalendarEventInput = {
  title: string;
  dateKey: string;
  endDateKey?: string | null;
  time?: string | null;
  endTime?: string | null;
  repeat: Exclude<CalendarRepeat, "recurring">;
  location?: string;
  details?: string;
  participantUids?: string[];
  showOnHome: boolean;
};

export function subscribeToCalendarEvents(
  householdId: string,
  onEvents: (events: FamilyCalendarEvent[]) => void,
  onError: () => void
): Unsubscribe {
  return onSnapshot(
    collection(db, "households", householdId, "events"),
    (snapshot) => {
      const events = snapshot.docs.map((snapshotDoc) => {
        const data = snapshotDoc.data();
        return {
          id: snapshotDoc.id,
          ...data,
          endDateKey: typeof data.endDateKey === "string" ? data.endDateKey : null,
          endTime: typeof data.endTime === "string" ? data.endTime : null,
          location: typeof data.location === "string" ? data.location : "",
          details: typeof data.details === "string" ? data.details : "",
          participantUids: Array.isArray(data.participantUids) ? data.participantUids : []
        } as FamilyCalendarEvent;
      });
      onEvents(events);
    },
    onError
  );
}

export async function saveCalendarEvent(
  householdId: string,
  userId: string,
  input: CalendarEventInput,
  event?: FamilyCalendarEvent | null
) {
  const eventRef = event
    ? doc(db, "households", householdId, "events", event.id)
    : doc(collection(db, "households", householdId, "events"));

  const base = {
    title: input.title.trim(),
    dateKey: input.dateKey,
    endDateKey: input.endDateKey || null,
    time: input.time || null,
    endTime: input.endTime || null,
    repeat: input.repeat,
    location: input.location?.trim() || "",
    details: input.details?.trim() || "",
    participantUids: input.participantUids || [],
    showOnHome: input.showOnHome,
    updatedAt: serverTimestamp()
  };

  if (event) {
    await setDoc(eventRef, base, { merge: true });
  } else {
    await setDoc(eventRef, {
      ...base,
      source: "manual",
      createdBy: userId,
      createdAt: serverTimestamp()
    });
  }

  return eventRef.id;
}

export async function deleteCalendarEvent(householdId: string, eventId: string) {
  await deleteDoc(doc(db, "households", householdId, "events", eventId));
}

export async function setCountdownVisibility(
  householdId: string,
  eventId: string,
  showOnHome: boolean
) {
  await setDoc(
    doc(db, "households", householdId, "events", eventId),
    { showOnHome, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

export function calendarDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function localDate(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

function dayDifference(left: Date, right: Date) {
  const leftUtc = Date.UTC(left.getFullYear(), left.getMonth(), left.getDate());
  const rightUtc = Date.UTC(right.getFullYear(), right.getMonth(), right.getDate());
  return Math.round((leftUtc - rightUtc) / 86_400_000);
}

export function eventOccursOn(event: FamilyCalendarEvent, day: Date) {
  const key = calendarDateKey(day);
  const start = localDate(event.dateKey);
  if (event.repeat === "none" || event.repeat === "recurring") {
    return key >= event.dateKey && key <= (event.endDateKey || event.dateKey);
  }
  if (dayDifference(day, start) < 0) return false;
  if (event.repeat === "daily") return true;
  if (event.repeat === "weekly") return day.getDay() === start.getDay();
  if (event.repeat === "monthly") return day.getDate() === start.getDate();
  return day.getMonth() === start.getMonth() && day.getDate() === start.getDate();
}

export function eventsForCalendarDay(events: FamilyCalendarEvent[], day: Date) {
  return events
    .filter((event) => eventOccursOn(event, day))
    .sort((left, right) => {
      if (!left.time && right.time) return -1;
      if (left.time && !right.time) return 1;
      return (left.time || "").localeCompare(right.time || "") || left.title.localeCompare(right.title);
    });
}

function parseLocalEventDate(event: FamilyCalendarEvent, date = event.dateKey) {
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = event.time?.split(":").map(Number) ?? [0, 0];
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

export function nextEventOccurrence(event: FamilyCalendarEvent, now: Date) {
  if (event.repeat === "none" || event.repeat === "recurring") return parseLocalEventDate(event);
  const candidate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  for (let offset = 0; offset <= 740; offset += 1) {
    if (eventOccursOn(event, candidate)) return parseLocalEventDate(event, calendarDateKey(candidate));
    candidate.setDate(candidate.getDate() + 1);
  }
  return parseLocalEventDate(event);
}

export function isUpcomingEvent(event: FamilyCalendarEvent, now: Date) {
  const occurrence = nextEventOccurrence(event, now);
  const endOfDay = new Date(occurrence);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay >= now;
}

export function sortUpcomingEvents(events: FamilyCalendarEvent[], now: Date) {
  return events
    .filter((event) => isUpcomingEvent(event, now))
    .sort((left, right) => nextEventOccurrence(left, now).getTime() - nextEventOccurrence(right, now).getTime());
}

export function countdownValue(event: FamilyCalendarEvent, now: Date) {
  const occurrence = nextEventOccurrence(event, now);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDay = new Date(occurrence.getFullYear(), occurrence.getMonth(), occurrence.getDate());
  const calendarDays = Math.round((eventDay.getTime() - today.getTime()) / 86_400_000);

  if (calendarDays === 0) {
    if (event.time && occurrence > now) {
      const hours = Math.max(1, Math.ceil((occurrence.getTime() - now.getTime()) / 3_600_000));
      return { value: String(hours), unit: hours === 1 ? "hour" : "hours" };
    }
    return { value: "Today!", unit: "" };
  }
  if (calendarDays >= 60) {
    const months = Math.max(2, Math.round(calendarDays / 30.44));
    return { value: String(months), unit: "months" };
  }
  if (calendarDays >= 14) {
    const weeks = Math.round(calendarDays / 7);
    return { value: String(weeks), unit: "weeks" };
  }
  return { value: String(calendarDays), unit: calendarDays === 1 ? "day" : "days" };
}
